import { useState, useCallback, useRef } from 'react';

/**
 * useMessage — shared hook for inline error/success/warning/info messages.
 *
 * Usage:
 *   const { message, type, showError, showSuccess, showWarning, clearMessage } = useMessage();
 *   <InlineMessage type={type} message={message} onDismiss={clearMessage} />
 *
 * showSuccess() auto-clears after 4 seconds unless you pass autoClose=false.
 */
export default function useMessage() {
  const [state, setState] = useState({ message: null, type: 'error' });
  const timerRef = useRef(null);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setState({ message: null, type: 'error' });
  }, []);

  const show = useCallback((message, type, autoClose = false) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setState({ message, type });
    if (autoClose) {
      timerRef.current = setTimeout(() => {
        setState({ message: null, type });
      }, 4000);
    }
  }, []);

  const showError   = useCallback((msg) => show(msg, 'error', false), [show]);
  const showSuccess = useCallback((msg) => show(msg, 'success', true), [show]);
  const showWarning = useCallback((msg) => show(msg, 'warning', false), [show]);
  const showInfo    = useCallback((msg) => show(msg, 'info', false), [show]);

  return {
    message: state.message,
    type: state.type,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    clearMessage: clear,
  };
}